/**
 * Where a resource from a `payload` block belongs — as a pure function.
 *
 * The sorting used to live in `processPayload` (`ChatExchange.tsx`), a
 * function that lives in the component, writes refs, updates the view and
 * scrolls up: none of that can be tested. The portal guide (stage 2) added
 * two decisions that *must* be tested — a `highlight` block must not produce
 * a tile, and a `navigate` block with `auto` may jump exactly once. So the
 * sorting now lives here, and `processPayload` does with the result what it
 * always did.
 *
 * Four destinations, as before:
 * - **sources** (`source`) → "Quellen" below the answer,
 * - **files** (`file`) → download rows at the end,
 * - **end-rendered** (`followUpSuggestions`, `toolChoices`, `actionChips`)
 *   → via `ResourceDetails`, because they need `onSubmitInput` and
 *   `isLastMessage`, which the inline renderer does not have,
 * - **inline** (chart, table, map, and from stage 2 also `navigate`) → as a
 *   placeholder `<div class="inline-resource" data-resource-id>` in the text.
 *
 * Plus the guide's two blocks:
 * - `highlight` is **intercepted**, not sorted: a visible tile without a
 *   click target would be junk (PORTAL_GUIDE.md §6.4). It goes into the
 *   pointer store once the message is complete.
 * - `navigate` takes the normal inline path — but only with a usable
 *   address. A block whose `route` fails validation disappears entirely: a
 *   placeholder for a button the renderer refuses would leave a gap in the
 *   text. The route is in the answer as a Markdown link anyway.
 */

import {
    isConfirmBlock,
    isHighlightBlock,
    isNavigateBlock,
    isQuestionBlock,
    isAutoNavigate,
    navigateHrefFrom,
    type HighlightBlock,
    type NavigateBlock,
} from '../../guide/guideBlocks';
import { isSourceResource } from '../../utils/sourceResource';

/** Types that `ResourceDetails` renders at the end of the message, not inline. */
export const END_RENDERED_RESOURCE_TYPES = ['followUpSuggestions', 'toolChoices', 'actionChips', 'approvalRequest'];

export function isEndRenderedResource(resource: any): boolean {
    return END_RENDERED_RESOURCE_TYPES.indexOf(resource?.type) >= 0;
}

export interface PayloadResourceGroups {
    sourceResources: any[];
    fileResources: any[];
    /** `followUpSuggestions` / `toolChoices` / `actionChips`. */
    endRenderedResources: any[];
    /** Everything with a placeholder in the text — including usable `navigate` blocks. */
    inlineResources: any[];
    /** The last reported `highlight` block; at most one per answer (§7.3). */
    highlight: HighlightBlock | null;
    /** The first `navigate` block with `auto: true` and a usable address. */
    autoNavigate: NavigateBlock | null;
    /** Its address — already validated, so the caller does not validate it twice. */
    autoNavigateHref: string | null;
}

/**
 * Sorts the resources of a payload. Anything that matches none of the types
 * ends up inline — just as before, so that a new type does not silently disappear.
 */
export function groupPayloadResources(resources: unknown): PayloadResourceGroups {
    const groups: PayloadResourceGroups = {
        sourceResources: [],
        fileResources: [],
        endRenderedResources: [],
        inlineResources: [],
        highlight: null,
        autoNavigate: null,
        autoNavigateHref: null,
    };
    if (!Array.isArray(resources)) return groups;

    for (const resource of resources) {
        if (isHighlightBlock(resource)) {
            // Intercepted: no placeholder, no tile — only the pointer.
            groups.highlight = resource;
            continue;
        }

        // Stage 3: the follow-up question and the confirmation card are drawn inline in the
        // message — like the navigation button, just without an auto action. No capping: one
        // question per answer comes from the server; it sends no more.
        if (isQuestionBlock(resource) || isConfirmBlock(resource)) {
            groups.inlineResources.push(resource);
            continue;
        }
        if (isNavigateBlock(resource)) {
            const href = navigateHrefFrom(resource);
            if (!href) continue;
            groups.inlineResources.push(resource);
            // At most one jumps: the server sends only one `navigate` per
            // answer, and two automatic jumps would be one too many anyway.
            if (!groups.autoNavigate && isAutoNavigate(resource)) {
                groups.autoNavigate = resource;
                groups.autoNavigateHref = href;
            }
            continue;
        }

        if (isSourceResource(resource)) {
            groups.sourceResources.push(resource);
            continue;
        }
        // A background job card sits with the files at the foot of the message — it has no
        // placeholder in the text, and inline it would vanish when the stream ends.
        if (resource?.type === 'file' || resource?.type === 'job') {
            groups.fileResources.push(resource);
            continue;
        }
        if (isEndRenderedResource(resource)) {
            groups.endRenderedResources.push(resource);
            continue;
        }
        groups.inlineResources.push(resource);
    }

    return groups;
}
